/**
 * voiceSegments — slices a flat Mastra message array into a presentation
 * sequence of `message` items and synthetic `voice-segment-header` items.
 *
 * Background: after [Plan 7a](../voice/...) a thread can interleave text
 * + voice turns. Voice-originated messages are tagged at persistence time
 * with an `id` prefix (`voice-user-...`, `voice-assistant-...`,
 * `voice-toolcall-...`, `voice-toolresult-...`, `voice-widget-...`) by
 * `ConciergeVoiceController.persistTranscriptsFromEvents`. This helper
 * walks the array, detects each non-voice → voice transition, and emits a
 * synthetic header before the voice run carrying duration / turn count /
 * start-time. The text chat renderer (`MessageListV2`) materializes these
 * headers as `<VoiceSegmentSeparatorCard>`.
 */

/** Detection: every voice-originated message id begins with `voice-`. */
export const isVoiceMessageId = (id: string | undefined | null): boolean =>
  typeof id === 'string' && id.startsWith('voice-');

interface VoiceSegmentCapableMessage {
  id?: string;
  role?: string;
  createdAt?: string | Date;
}

export interface VoiceSegmentHeader {
  kind: 'voice-segment-header';
  segmentKey: string;
  durationSec: number;
  turnCount: number;
  tWallStartIso: string;
  messageCount: number;
}

export interface MessageDisplayItem<T> {
  kind: 'message';
  message: T;
  /** Non-null when the message belongs to a voice segment; matches the
   *  matching header's `segmentKey`. Drives the hide/show toggle. */
  segmentKey: string | null;
}

export type DisplayItem<T> = VoiceSegmentHeader | MessageDisplayItem<T>;

const toIsoString = (value: string | Date | undefined): string => {
  if (!value) return '';
  if (value instanceof Date) return value.toISOString();
  return value;
};

const toEpochMs = (value: string | Date | undefined): number => {
  if (!value) return NaN;
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? NaN : parsed;
};

/**
 * Walk the message array and emit `voice-segment-header` synthetic items
 * before each contiguous run of voice messages. Non-voice messages are
 * passed through with `segmentKey: null`. Voice messages are passed
 * through with `segmentKey` matching the run's header.
 *
 * Stable keys: the `segmentKey` is `voice-segment-${index}` where index
 * increments per detected voice run. As long as the input array shape is
 * stable, the keys are stable across renders. The caller is responsible
 * for invalidating any per-segment UI state (e.g. hide-toggle Set) when
 * the surrounding `conversationId` changes.
 */
export function buildVoiceSegments<T extends VoiceSegmentCapableMessage>(messages: T[]): DisplayItem<T>[] {
  const out: DisplayItem<T>[] = [];
  let i = 0;
  let segmentIndex = 0;

  while (i < messages.length) {
    const current = messages[i];
    if (!isVoiceMessageId(current.id)) {
      out.push({ kind: 'message', message: current, segmentKey: null });
      i += 1;
      continue;
    }

    // Found the start of a voice run. Walk forward until the run ends.
    const runStart = i;
    let runEnd = i;
    while (runEnd < messages.length && isVoiceMessageId(messages[runEnd].id)) {
      runEnd += 1;
    }

    const segmentKey = `voice-segment-${segmentIndex++}`;
    const runMessages = messages.slice(runStart, runEnd);

    const firstTs = toEpochMs(runMessages[0]?.createdAt);
    const lastTs = toEpochMs(runMessages[runMessages.length - 1]?.createdAt);
    const durationSec =
      Number.isFinite(firstTs) && Number.isFinite(lastTs) && lastTs >= firstTs
        ? Math.max(0, Math.round((lastTs - firstTs) / 1000))
        : 0;
    const turnCount = runMessages.filter((m) => m.role === 'user').length;
    const tWallStartIso = toIsoString(runMessages[0]?.createdAt);

    out.push({
      kind: 'voice-segment-header',
      segmentKey,
      durationSec,
      turnCount,
      tWallStartIso,
      messageCount: runMessages.length,
    });

    for (const m of runMessages) {
      out.push({ kind: 'message', message: m, segmentKey });
    }

    i = runEnd;
  }

  return out;
}

/** Format a duration like "3 min 14 sec" / "47 sec" / "1 hr 2 min". */
export function formatVoiceSegmentDuration(durationSec: number): string {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return '0 sec';
  const totalSec = Math.round(durationSec);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} hr`);
  if (minutes > 0) parts.push(`${minutes} min`);
  if (seconds > 0 && hours === 0) parts.push(`${seconds} sec`);
  if (parts.length === 0) parts.push(`${totalSec} sec`);
  return parts.join(' ');
}

/** Format an ISO string into the user's locale short time (e.g. "5:42 PM"). */
export function formatVoiceSegmentStartTime(tWallStartIso: string): string {
  if (!tWallStartIso) return '';
  const ms = Date.parse(tWallStartIso);
  if (Number.isNaN(ms)) return '';
  try {
    return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
}
